import { useState } from "preact/hooks";
import { createPortal } from "preact/compat";
import { idx, resolved, analysis, planTerm, drawerId, flash, unitColor } from "../store";
import { active, state, update, setActive, addSchedule, removeSchedule } from "../lib/state";
import { DAYS, kindGroups, defaultSel, laneLayout, PALETTE } from "../lib/planner";
import { fmt } from "../lib/text";
import { day, kindLabel, t } from "../i18n";
import type { Day, Slot, Term } from "../lib/types";
import { ContactsDialog } from "./dialogs/Contacts";
import { ShareDialog } from "./dialogs/Share";
import { ExportDialog } from "./dialogs/Export";

type DialogName = "contacts" | "share" | "export" | "event" | null;

export function Planner() {
  const I = idx.value!;
  const sched = active.value;
  const { slots, auto, warnings } = resolved.value;
  const { clashes, transfers, clashIds } = analysis.value;
  const [dialog, setDialog] = useState<DialogName>(null);
  const [pop, setPop] = useState<{ slot: Slot; x: number; y: number } | null>(null);
  const term = planTerm.value;

  const setTerm = (tm: Term) => { planTerm.value = tm; update((s) => { s.term = tm; }); };
  const termPicks = sched.picks.filter((p) => { const c = I.courses.get(p.id); return c && (!c.term || c.term === term); });
  const offTerm = sched.picks.map((p) => I.courses.get(p.id)).filter((c) => c?.term && c.term !== term);

  const alerts: { cls: string; label: string; text: string }[] = [];
  const seen = new Set<string>();
  for (const [a, b] of clashes) {
    const k = [a.courseId || a.id, b.courseId || b.id].sort().join("|");
    if (seen.has(k)) continue;
    seen.add(k);
    alerts.push({ cls: "", label: t("clash"), text: `${a.title} (${a.kind}) ⟷ ${b.title} (${b.kind}) · ${day(a.day)} ${fmt(Math.max(a.start, b.start))}–${fmt(Math.min(a.end, b.end))}` });
  }
  if (!clashes.length && slots.length) alerts.push({ cls: "ok", label: t("clear"), text: `Bez preklapanja (${termPicks.length} predmeta).` });
  for (const [a, b, g] of transfers.slice(0, 4)) {
    const [x, y] = a.end <= b.start ? [a, b] : [b, a];
    alerts.push({ cls: "warn", label: t("transfer"), text: `${g.gap} min od ${x.fac} do ${y.fac} (${day(x.day)}), a pješice treba oko ${g.need} min.` });
  }
  const stale = slots.filter((s) => s.stale);
  if (stale.length) alerts.push({ cls: "warn", label: t("heads"), text: `Službeni raspored se promijenio za ${stale.length} termin(a) koje ste ručno uredili. Provjerite ih.` });
  for (const w of warnings) alerts.push({ cls: "warn", label: t("heads"), text: w });
  if (offTerm.length) alerts.push({ cls: "warn", label: "Drugi semestar", text: `${offTerm.map((c) => c!.name).join(", ")}: ${term === "winter" ? "ljetni" : "zimski"} semestar.` });

  return (
    <aside class="planner" aria-label={t("myWeek")}>
      <div class="planner-head">
        <h2>{t("myWeek")}</h2>
        <div class="seg">
          {(["winter", "summer"] as Term[]).map((tm) => <button key={tm} class={term === tm ? "on" : ""} onClick={() => setTerm(tm)}>{t(tm)}</button>)}
        </div>
      </div>
      <div class="sched-tabs">
        {state.value.schedules.map((s, i) => (
          <button key={i} class={i === state.value.active ? "on" : ""} onClick={() => setActive(i)}
            onDblClick={() => { const n = prompt("Naziv rasporeda", s.name); if (n) update((x) => { x.name = n; }); }}>{s.name}</button>
        ))}
        <button class="plus" title="Novi plan (kopija trenutnog)" onClick={() => addSchedule({ ...structuredClone(sched), name: `Plan ${String.fromCharCode(65 + state.value.schedules.length)}` })}>+ plan</button>
        {state.value.schedules.length > 1 && <button title="Obriši ovaj raspored" onClick={() => confirm(`Obrisati „${sched.name}“?`) && removeSchedule(state.value.active)}>×</button>}
      </div>
      <div class="alerts">{alerts.map((a, i) => <div key={i} class={`alert ${a.cls}`}><b>{a.label}</b>{a.text}</div>)}</div>
      <WeekGrid slots={slots} clashIds={clashIds} onPick={(slot, e) => setPop({ slot, x: e.clientX, y: e.clientY })} />
      <PickList autoSel={auto} />
      <div class="planner-actions">
        <button onClick={() => setDialog("event")}>{t("addEvent")}</button>
        <button onClick={() => setDialog("contacts")} disabled={!sched.picks.length}>{t("teachersEmails")}</button>
        <button onClick={() => setDialog("export")} disabled={!slots.length}>{t("export")}</button>
        <button class="ghost" onClick={() => setDialog("share")} disabled={!sched.picks.length && !sched.custom.length}>{t("share")}</button>
      </div>
      {pop && createPortal(<SlotPopover {...pop} onClose={() => setPop(null)} />, document.body)}
      {dialog === "contacts" && <ContactsDialog ids={sched.picks.map((p) => p.id)} onClose={() => setDialog(null)} />}
      {dialog === "share" && <ShareDialog onClose={() => setDialog(null)} />}
      {dialog === "export" && <ExportDialog onClose={() => setDialog(null)} />}
      {dialog === "event" && createPortal(<EventDialog onClose={() => setDialog(null)} />, document.body)}
    </aside>
  );
}

function WeekGrid({ slots, clashIds, onPick }: { slots: Slot[]; clashIds: Set<string>; onPick: (s: Slot, e: MouseEvent) => void }) {
  if (!slots.length) return <div class="cal-wrap"><div class="cal-empty">{t("empty")}</div></div>;
  const days = DAYS.filter((d, i) => i < 5 || slots.some((s) => s.day === d));
  const lo = Math.min(8 * 60, Math.floor(Math.min(...slots.map((s) => s.start)) / 60) * 60);
  const hi = Math.max(18 * 60, Math.ceil(Math.max(...slots.map((s) => s.end)) / 60) * 60);
  const PX = 0.95, H = (hi - lo) * PX;
  const lanes = laneLayout(slots);
  const ticks: number[] = []; for (let m = lo; m <= hi; m += 60) ticks.push(m);
  const lines: number[] = []; for (let m = lo; m < hi; m += 30) lines.push(m);
  return (
    <div class="cal-wrap">
      <div class="cal" style={`grid-template-columns:46px repeat(${days.length},1fr)`}>
        <div class="hd corner" />
        {days.map((d) => <div class="hd" key={d}>{day(d)}</div>)}
        <div class="gut" style={`height:${H}px`}>{ticks.map((m) => <span class="tick" key={m} style={`top:${(m - lo) * PX}px`}>{fmt(m)}</span>)}</div>
        {days.map((d) => (
          <div class="col" key={d} style={`height:${H}px`}>
            {lines.map((m) => <div key={m} class={`line ${m % 60 ? "half" : ""}`} style={`top:${(m - lo) * PX}px`} />)}
            {slots.filter((s) => s.day === d).map((s) => {
              const L = lanes.get(s.id)!, w = 100 / L.lanes;
              const old = s.academicYear && s.academicYear !== idx.value!.core.calendar.academic_year;
              return (
                <div key={s.id} class={`blk ${clashIds.has(s.id) ? "clash" : ""} ${old ? "old" : ""} ${s.edited ? "edited" : ""} ${s.stale ? "stale" : ""} ${s.kind === "custom" ? "custom" : ""}`}
                  style={`--bc:${s.color};top:${(s.start - lo) * PX}px;height:${Math.max(18, (s.end - s.start) * PX - 2)}px;left:calc(${L.lane * w}% + 2px);width:calc(${w}% - 4px)`}
                  title={`${s.title}\n${kindLabel(s.kind)} ${s.group && s.group !== "-" ? s.group : ""}\n${day(s.day)} ${fmt(s.start)}–${fmt(s.end)}${s.room ? " · " + s.room : ""}${s.lecturer ? "\n" + s.lecturer : ""}`}
                  onClick={(e) => onPick(s, e as unknown as MouseEvent)}>
                  <b><span class="k">{s.kind === "custom" ? "★" : s.kind}</span>{s.title}</b>
                  <span class="t">{fmt(s.start)}–{fmt(s.end)}{s.room ? ` · ${s.room}` : ""}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function SlotPopover({ slot, x, y, onClose }: { slot: Slot; x: number; y: number; onClose: () => void }) {
  const I = idx.value!;
  const isCustom = slot.kind === "custom";
  const base = isCustom ? null : I.sessionsByKey.get(slot.id);
  const [start, setStart] = useState(fmt(slot.start));
  const [end, setEnd] = useState(fmt(slot.end));
  const [d, setD] = useState<Day>(slot.day);
  const [room, setRoom] = useState(slot.room || "");
  const [note, setNote] = useState(slot.note || "");
  const left = Math.max(8, Math.min(x, window.innerWidth - 316)), top = Math.max(8, Math.min(y, window.innerHeight - 340));
  const save = () => {
    update((s) => {
      if (isCustom) {
        const ev = s.custom.find((e) => `custom:${e.id}` === slot.id);
        if (ev) Object.assign(ev, { start, end, day: d, place: room });
      } else if (base) {
        s.edits[slot.id] = { start, end, day: d, room: room || undefined, note: note || undefined, base: { start: base.start, end: base.end, day: base.day, room: base.room ?? null } };
      }
    });
    flash("Sačuvano"); onClose();
  };
  return (
    <>
      <div class="scrim" style="background:transparent" onClick={onClose} />
      <div class="pop" style={`left:${left}px;top:${top}px`}>
        <h4>{slot.title}</h4>
        <div class="note">{kindLabel(slot.kind)} {slot.group && slot.group !== "-" ? `· ${slot.group}` : ""} {slot.lecturer ? `· ${slot.lecturer}` : ""}</div>
        {slot.stale && <div class="alert warn" style="margin-top:.4rem"><b>Pažnja</b>Službeni termin se promijenio nakon vaše izmjene{base ? ` (sada ${day(base.day)} ${base.start}–${base.end}${base.room ? " · " + base.room : ""})` : ""}.</div>}
        <div class="row">
          <select value={d} onChange={(e) => setD((e.target as HTMLSelectElement).value as Day)}>{DAYS.map((x) => <option key={x} value={x}>{day(x)}</option>)}</select>
          <input type="time" value={start} onInput={(e) => setStart((e.target as HTMLInputElement).value)} />
          <input type="time" value={end} onInput={(e) => setEnd((e.target as HTMLInputElement).value)} />
        </div>
        <div class="row"><input placeholder="sala / mjesto" value={room} onInput={(e) => setRoom((e.target as HTMLInputElement).value)} style="flex:1" /></div>
        {!isCustom && <div class="row"><input placeholder="bilješka" value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)} style="flex:1" /></div>}
        <div class="row">
          <button class="btn sm" onClick={save}>Sačuvaj</button>
          {!isCustom && <button class="btn ghost sm" onClick={() => { update((s) => { s.edits[slot.id] = { hidden: true }; }); onClose(); }}>Sakrij</button>}
          {!isCustom && slot.edited && <button class="btn ghost sm" onClick={() => { update((s) => { delete s.edits[slot.id]; }); onClose(); }}>Vrati službeno</button>}
          {isCustom && <button class="btn ghost sm" onClick={() => { update((s) => { s.custom = s.custom.filter((e) => `custom:${e.id}` !== slot.id); }); onClose(); }}>Obriši</button>}
          {slot.courseId && <button class="btn ghost sm" onClick={() => { drawerId.value = slot.courseId!; onClose(); }}>Predmet</button>}
        </div>
      </div>
    </>
  );
}

function PickList({ autoSel }: { autoSel: { id: string; kind: string; group: string }[] }) {
  const I = idx.value!;
  const sched = active.value;
  const hiddenCount = (id: string) => Object.entries(sched.edits).filter(([k, e]) => e.hidden && k.startsWith(id + "|")).length;
  return (
    <div class="picks">
      {sched.picks.map((p, i) => {
        const c = I.courses.get(p.id);
        if (!c) return null;
        const kg = kindGroups(I, p.id, planTerm.value);
        const color = p.color || PALETTE[i % PALETTE.length];
        return (
          <div class="pick" key={p.id} style={`--bc:${color}`}>
            <label class="sw" style="cursor:pointer;position:relative" title="Boja">
              <input type="color" value={color} style="opacity:0;position:absolute;inset:0;width:12px;height:12px" onInput={(e) => update((s) => { s.picks[i].color = (e.target as HTMLInputElement).value; })} />
            </label>
            <div>
              <h4 onClick={() => (drawerId.value = c.id)}>{p.label || c.name} <span class="meta" style="display:inline"><span class="fac" style={`color:${unitColor(c.fac)}`}>{c.fac}</span></span></h4>
              {Object.keys(kg).length ? (
                <div class="opts">
                  {Object.entries(kg).map(([k, groups]) => {
                    const names = Object.keys(groups!);
                    const cur = p.sel[k] ?? defaultSel(groups!);
                    const a = autoSel.find((x) => x.id === p.id && x.kind === k);
                    return (
                      <label key={k}>{k}
                        <select value={cur} onChange={(e) => update((s) => { s.picks[i].sel[k] = (e.target as HTMLSelectElement).value; })}>
                          {names.length > 1 && <option value="auto">auto{a ? ` → ${a.group}` : ""}</option>}
                          <option value="all">svi termini</option>
                          {names.length > 1 && names.map((g) => <option key={g} value={g}>{g}</option>)}
                          <option value="off">preskoči</option>
                        </select>
                      </label>
                    );
                  })}
                  {hiddenCount(p.id) > 0 && <button class="linkish" onClick={() => update((s) => { for (const k of Object.keys(s.edits)) if (k.startsWith(p.id + "|") && s.edits[k].hidden) delete s.edits[k]; })}>prikaži sakrivene ({hiddenCount(p.id)})</button>}
                  <button class="linkish" onClick={() => { const n = prompt("Kratki naziv u rasporedu", p.label || c.name); if (n !== null) update((s) => { s.picks[i].label = n || undefined; }); }}>preimenuj</button>
                </div>
              ) : <div class="nosched">{c.term && c.term !== planTerm.value ? `${c.term === "winter" ? "Zimski" : "Ljetni"} semestar.` : "Nema termina. Pitajte nastavnika."}</div>}
            </div>
            <button class="x" title="Ukloni" onClick={() => update((s) => { s.picks.splice(i, 1); })}>×</button>
          </div>
        );
      })}
      {sched.custom.filter((e) => e.term === planTerm.value).map((ev) => (
        <div class="pick" key={ev.id} style={`--bc:${ev.color || "#555"}`}>
          <span class="sw" />
          <div><h4>★ {ev.title}</h4><div class="nosched">{day(ev.day)} {ev.start}–{ev.end}{ev.place ? ` · ${ev.place}` : ""}</div></div>
          <button class="x" onClick={() => update((s) => { s.custom = s.custom.filter((e) => e.id !== ev.id); })}>×</button>
        </div>
      ))}
    </div>
  );
}

function EventDialog({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [d, setD] = useState<Day>("Mon");
  const [start, setStart] = useState("18:00");
  const [end, setEnd] = useState("19:30");
  const [place, setPlace] = useState("");
  const [color, setColor] = useState("#555555");
  const add = () => {
    update((s) => { s.custom.push({ id: Math.random().toString(36).slice(2, 9), title, day: d, start, end, place: place || undefined, color, term: planTerm.value }); });
    flash("Događaj dodan"); onClose();
  };
  return (
    <div class="scrim" onClick={(e) => e.target === e.currentTarget && onClose()} style="display:grid;place-items:center;z-index:70">
      <div class="pop" style="position:static;width:min(380px,92vw)">
        <h4>Vlastiti događaj</h4>
        <div class="row"><input placeholder="npr. Posao, Teretana" value={title} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} style="flex:1" /></div>
        <div class="row">
          <select value={d} onChange={(e) => setD((e.target as HTMLSelectElement).value as Day)}>{DAYS.map((x) => <option key={x} value={x}>{day(x)}</option>)}</select>
          <input type="time" value={start} onInput={(e) => setStart((e.target as HTMLInputElement).value)} />
          <input type="time" value={end} onInput={(e) => setEnd((e.target as HTMLInputElement).value)} />
          <input type="color" value={color} onInput={(e) => setColor((e.target as HTMLInputElement).value)} />
        </div>
        <div class="row"><input placeholder="mjesto (opcionalno)" value={place} onInput={(e) => setPlace((e.target as HTMLInputElement).value)} style="flex:1" /></div>
        <div class="row"><button class="btn sm" disabled={!title.trim()} onClick={add}>Dodaj</button><button class="btn ghost sm" onClick={onClose}>Odustani</button></div>
      </div>
    </div>
  );
}
